import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import Appointments from '../Appointments';
import CallSection from '../CallSection';
import NoAppointments from '../NoAppointments';
import ProviderImage from '../../../ProviderImage';

import { ViewProps } from '../common';
import { getProviderNameFull } from '../../../../utils/provider';
import { ReactComponent as MapPinSvg } from '../../../../assets/map-pin.svg';
import { constructAddress } from '../../../../utils/practice';

import styles from '../../index.module.css';
import providerViewStyles from './index.module.css';

const ProviderView: React.FC<ViewProps> = ({
  availableEhrTimeSlots,
  practices,
  providers,
  navigateToNextPage,
  onAppointmentSelected,
  practice,
  encounter,
  fieldConfig,
}) => {
  const { t } = useTranslation();
  const [practiceAndProviderIds, setPracticeAndProviderIds] = useState<
    Set<string>
  >(new Set([]));

  useEffect(() => {
    const practiceAndProviderIds: Set<string> = new Set();
    availableEhrTimeSlots.forEach((timeSlot) =>
      practiceAndProviderIds.add(
        `${timeSlot.providerId},${timeSlot.practiceId}`,
      ),
    );
    setPracticeAndProviderIds(practiceAndProviderIds);
  }, [availableEhrTimeSlots, practices]);

  if (!practiceAndProviderIds.size) {
    return (
      <NoAppointments
        navigateToNextPage={navigateToNextPage}
        practice={practice}
        encounter={encounter}
        fieldConfig={fieldConfig}
      />
    );
  }

  return (
    <>
      {[...practiceAndProviderIds].map((tuple, index) => {
        const [providerId, practiceId] = tuple.split(',');
        const provider = providers[providerId];
        const practice = practices[practiceId];
        const practiceAddress = constructAddress(practice);

        return (
          <div
            className={classNames(styles.ProviderContainer, {
              [styles.BorderBottom]:
                fieldConfig.scheduleAppointmentCallSectionEnabled ||
                index + 1 !== practiceAndProviderIds.size,
            })}
            key={tuple}
          >
            {
              <div className={styles.ProviderInfo}>
                <ProviderImage
                  provider={provider}
                  className={providerViewStyles.ProviderImage}
                  size="Small"
                />
                <div className={styles.ProviderInfoText}>
                  <span>{getProviderNameFull({ provider })}</span>
                  <div className={styles.MultiProviderSpecialty}>
                    {practice.name}
                  </div>
                  {practiceAddress && (
                    <div className={styles.ProviderAdditional}>
                      <MapPinSvg />
                      {practiceAddress}
                    </div>
                  )}
                </div>
              </div>
            }
            <Appointments
              timeSlots={availableEhrTimeSlots}
              providerId={providerId}
              practiceId={practiceId}
              timezone={practice.timezone}
              onAppointmentSelected={onAppointmentSelected}
            />
          </div>
        );
      })}
      {fieldConfig.previsitFormFieldId &&
        fieldConfig.scheduleAppointmentCallSectionEnabled &&
        practice && (
          <CallSection
            navigateToNextPage={navigateToNextPage}
            practice={practice}
            previsitFormFieldId={fieldConfig.previsitFormFieldId}
            label={t('Not finding your ideal availability?')}
          />
        )}
    </>
  );
};

export default ProviderView;
